import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Grid, Paper, Tooltip, Typography, } from "@mui/material";
import { DataGrid, } from "@mui/x-data-grid";
import { Layout } from "../../components/Layout/Layout";
import { LeadgenerationTopHeader } from "./LeadgenerationTopHeader";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { statusBgColor, statusMapping, statusTextColor, } from "../../../application/common/utils/column-helper";
import LeadsApi from "../../../infrastructure/api/pages/lead-api";
import { Star } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AssignMultipleLeadsModal from "./AssignMultipleLeadsModal";
import UsersApi from "../../../infrastructure/api/pages/users-api";
import { convertToNLEuroFormat } from "../../../application/common/utils/helper";
import { filterFieldSetter, useSessionFilterMemory, } from "../../hooks/useSessionFilterMemory";
import { leadStatusConfig } from "../../../application/common/utils/lead-status-helper";
const DEFAULT_LEADS_FILTERS = {
    search: "",
    status: undefined,
    selectedPersonId: null,
    isSelectiveRoles: true,
    isUnAssignedLeads: false,
    page: 0,
    pageSize: 15,
    sortModel: [],
};
// The grid's own page size (15) plus the sizes its footer selector offers.
const ALLOWED_LEAD_PAGE_SIZES = [15, 25, 50, 100];
// Only these reach the backend's @sortField — anything else stored (a renamed or
// removed column from an older session) is dropped rather than forwarded.
const SORTABLE_LEAD_FIELDS = [
    "leadNumber",
    "companyName",
    "totalObjects",
    "status",
    "responsiblePersonName",
    "totalOutstandingOffers",
    "totalOutstandingOffersAmount",
    "totalSignedOffers",
    "totalSignedOffersAmount",
];
// Sanity-checks the stored payload before it ever reaches the API. Anything the
// screen can no longer represent (a status code retired since the session began,
// a sort on a dropped column, a negative page) falls back to its default.
const revalidateLeadsFilters = (restored) => {
    const status = typeof restored.status === "number" &&
        leadStatusConfig.some((config) => config.value === restored.status)
        ? restored.status
        : undefined;
    const sortModel = (Array.isArray(restored.sortModel) ? restored.sortModel : [])
        .filter((item) => item &&
        typeof item.field === "string" &&
        SORTABLE_LEAD_FIELDS.includes(item.field) &&
        (item.sort === "asc" || item.sort === "desc"))
        .slice(0, 1);
    const pageSize = ALLOWED_LEAD_PAGE_SIZES.includes(restored.pageSize)
        ? restored.pageSize
        : DEFAULT_LEADS_FILTERS.pageSize;
    return Object.assign(Object.assign({}, restored), { search: typeof restored.search === "string" ? restored.search : "", status, selectedPersonId: typeof restored.selectedPersonId === "string"
            ? restored.selectedPersonId
            : null, isSelectiveRoles: typeof restored.isSelectiveRoles === "boolean"
            ? restored.isSelectiveRoles
            : DEFAULT_LEADS_FILTERS.isSelectiveRoles, isUnAssignedLeads: restored.isUnAssignedLeads === true, page: Number.isInteger(restored.page) && restored.page >= 0
            ? restored.page
            : 0, pageSize,
        sortModel });
};
export const Leadgeneration = ({ title, hideActions, isAffiliate = false } = {}) => {
    const { t: translate } = useTranslation();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [refreshModalData, setRefreshModalData] = useState(false);
    const [totalLeads, setTotalLeads] = useState(0);
    const navigate = useNavigate();
    const [contactOptions, setContactOptions] = useState([]);
    const previousPaginationRef = useRef(null);
    // Verkoop and Affiliate render this same component with different data
    // (isAffiliate), so they deliberately keep separate filter memory — confirmed
    // with the client. Sharing one entry would make switching tabs feel like the
    // other screen had silently changed its filters.
    const [filters, setFilters] = useSessionFilterMemory(isAffiliate ? "affiliate" : "leads", DEFAULT_LEADS_FILTERS, revalidateLeadsFilters);
    // Memoised so the DataGrid isn't handed a brand-new object on every render.
    const paginationModel = useMemo(() => ({ page: filters.page, pageSize: filters.pageSize }), [filters.page, filters.pageSize]);
    // Existing filter components keep their setter shape; the page-reset each one
    // already performed is preserved exactly (note that Status deliberately does
    // NOT reset the page — unchanged from before filter memory).
    const setSearch = filterFieldSetter(setFilters, "search");
    const setStatus = filterFieldSetter(setFilters, "status");
    const setIsSelectiveRoles = filterFieldSetter(setFilters, "isSelectiveRoles");
    const setIsUnAssignedLeads = filterFieldSetter(setFilters, "isUnAssignedLeads");
    const setSelectedPerson = (person) => setFilters((prev) => { var _a; return (Object.assign(Object.assign({}, prev), { selectedPersonId: (_a = person === null || person === void 0 ? void 0 : person.id) !== null && _a !== void 0 ? _a : null })); });
    const setPaginationModel = (model) => setFilters((prev) => (Object.assign(Object.assign({}, prev), { page: model.page, pageSize: model.pageSize })));
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const userId = !filters.selectedPersonId || filters.selectedPersonId === "all"
            ? ""
            : filters.selectedPersonId;
        setLoading(true);
        // Unchanged on purpose: the ref is null on mount, so the first request of a
        // page load always reports isPageChanged=true. spGetLeads_v3 forces @page=0
        // whenever a search term arrives with isPageChanged=0 — with a restored
        // "search + page 3" that would silently serve page 0 while the grid showed
        // page 3.
        let pageChanged = ((_a = previousPaginationRef.current) === null || _a === void 0 ? void 0 : _a.page) !== paginationModel.page;
        const sortField = (_b = filters.sortModel[0]) === null || _b === void 0 ? void 0 : _b.field;
        const sortOrder = ((_c = filters.sortModel[0]) === null || _c === void 0 ? void 0 : _c.sort) === "asc" ||
            ((_d = filters.sortModel[0]) === null || _d === void 0 ? void 0 : _d.sort) === "desc"
            ? (_e = filters.sortModel[0]) === null || _e === void 0 ? void 0 : _e.sort
            : undefined;
        LeadsApi.getAllLeadsWithContractInfo(paginationModel.page, paginationModel.pageSize, filters.search, filters.status, userId, filters.isSelectiveRoles, pageChanged, filters.isUnAssignedLeads, sortField, sortOrder, isAffiliate)
            .then((response) => {
            if (response.status === 200 && response.data != null) {
                setLeads(response.data);
                let data = response.data;
                if (data.length > 0) {
                    setTotalLeads(data[0].totalRows);
                }
                else if (filters.page > 0) {
                    // Restored (or stale) page now past the end of the result set — e.g.
                    // leads were reassigned away while the user was on another screen.
                    // Fall back to the first page instead of showing an empty grid.
                    setTotalLeads(0);
                    setFilters((prev) => (Object.assign(Object.assign({}, prev), { page: 0 })));
                }
                else {
                    setTotalLeads(0);
                }
            }
        })
            .finally(() => {
            setTimeout(() => setLoading(false), 400);
        });
        previousPaginationRef.current = paginationModel;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, refreshModalData, isAffiliate]);
    ////New thing to add
    const columnsForDataGrid = [
        {
            field: "leadNumber",
            headerName: "Nummer",
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            minWidth: 100,
            renderCell: (params) => (_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center"
                }, children: [params.row.isNewLeadImport && ( // Check if the row is new
                    (_jsx(Star, { sx: { color: "gold", marginRight: "5px" } })) // Add the star icon with styling
                    ), params.row.leadNumber] })),
        },
        {
            field: "companyName",
            headerName: translate("Company"),
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            minWidth: 250,
        },
        {
            field: "totalObjects",
            headerName: translate("TotalObjects"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            flex: 1,
            minWidth: 100,
        },
        {
            field: "status",
            headerName: translate("Status"),
            sortable: true,
            align: "center",
            headerAlign: "center",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            minWidth: 200,
            renderCell: (params) => (_jsx(Grid, { container: true, sx: {
                    alignContent: "center",
                    justifyContent: "center"
                }, children: _jsx(Grid, { children: _jsx(Box, { sx: {
                            bgcolor: statusBgColor[params.row.status],
                            padding: 1,
                            mt: 1,
                            borderRadius: 2
                        }, children: _jsx(Tooltip, { title: translate(`${statusMapping[params.row.status]}`), children: _jsx(Typography, { sx: {
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    display: "block",
                                    maxWidth: 170,
                                    color: statusTextColor[params.row.status],
                                }, variant: "h5", children: translate(`${statusMapping[params.row.status]}`) }) }) }) }) })),
        },
        {
            field: "responsiblePersonName",
            headerName: translate("ResponsiblePerson"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            flex: 1,
            minWidth: 200,
        },
        {
            field: "totalOutstandingOffers",
            headerName: translate("TotalOutstandingOffers"),
            sortable: true,
            align: "center",
            headerAlign: "center",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (_jsx(Typography, { variant: "body2", align: "center", children: params.value || "0" })),
        },
        {
            field: "totalOutstandingOffersAmount",
            headerName: translate("TotalOutstandingOffersAmount"),
            sortable: true,
            align: "center",
            headerAlign: "center",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Typography, { sx: {
                    color: statusTextColor[3],
                    fontWeight: 500,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }, children: convertToNLEuroFormat(params.value || "0") })),
        },
        {
            field: "totalSignedOffers",
            headerName: translate("TotalSignedOffers"),
            sortable: true,
            align: "center",
            headerAlign: "center",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (_jsx(Typography, { variant: "body2", align: "center", children: params.value || "0" })),
        },
        {
            field: "totalSignedOffersAmount",
            headerName: translate("TotalSignedOffersAmount"),
            sortable: true,
            align: "center",
            headerAlign: "center",
            hideable: false,
            filterable: false,
            hideSortIcons: false,
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Typography, { sx: {
                    color: statusTextColor[3],
                    fontWeight: 500,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }, children: convertToNLEuroFormat(params.value || "0") })),
        },
    ];
    const handleCellClick = (params, event) => {
        const lead = params.row;
        if (lead) {
            const id = lead.id;
            const companyId = lead.companyId;
            const orderNumber = lead.leadNumber;
            const query = new URLSearchParams({
                id,
                companyId,
                orderNumber,
            });
            // Preserve the affiliate origin so the detail page keeps the Affiliate context
            // (sidebar highlight + breadcrumb/back target). Query param survives the
            // breadcrumb's full-page navigation, unlike router state.
            if (isAffiliate) {
                query.set("isAffiliate", "true");
            }
            const newUrl = `/lead-detail?${query.toString()}`;
            navigate(newUrl);
        }
    };
    useEffect(() => {
        UsersApi.getResponsiblePersons(false)
            .then((response) => {
            if (response.status === 200 && Array.isArray(response.data)) {
                const op = response.data.map((item) => {
                    return {
                        id: item.id,
                        label: `${item.firstName} ${item.lastName}`,
                        // map other properties if needed
                    };
                });
                // Define the "All" option
                const allOption = {
                    id: "all",
                    label: translate("All"),
                };
                setContactOptions([allOption, ...op]);
            }
        })
            .finally(() => { });
    }, [translate]);
    // Second half of the stale-value handling: a restored person id can only be
    // checked once the options are loaded. Someone who was deleted, deactivated or
    // no longer holds a Sales Agent role is simply dropped back to "everyone" —
    // silently, since the user didn't do anything wrong.
    useEffect(() => {
        if (!contactOptions.length)
            return;
        const personId = filters.selectedPersonId;
        if (!personId || personId === "all")
            return;
        if (!contactOptions.some((item) => item.id === personId)) {
            setFilters((prev) => (Object.assign(Object.assign({}, prev), { selectedPersonId: null, page: 0 })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactOptions]);
    const handleOpenModal = () => {
        setModalOpen(true);
    };
    const handleCloseModal = () => {
        setModalOpen(false);
    };
    return (_jsxs(Layout, { children: [_jsx(LeadgenerationTopHeader, { title: title, hideActions: hideActions, search: filters.search, status: filters.status, setSearch: setSearch, setStatus: setStatus, handleOpenModal: handleOpenModal, setRefresh: setRefreshModalData, isSelectiveRoles: filters.isSelectiveRoles, setIsSelectiveRoles: setIsSelectiveRoles, setPaginationModel: setPaginationModel, selectedPersonId: filters.selectedPersonId, setSelectedPerson: setSelectedPerson, isUnAssignedLeads: filters.isUnAssignedLeads, setIsUnAssignedLeads: setIsUnAssignedLeads, contactOptions: contactOptions }), _jsx(Paper, { sx: {
                    marginTop: "1%",
                    padding: "16px 32px",
                    borderRadius: "24px",
                }, children: _jsx(Paper, { sx: {
                        width: "100%",
                        overflow: "hidden",
                        boxShadow: "none",
                    }, children: _jsx(DataGrid, { loading: loading, rows: leads !== null && leads !== void 0 ? leads : [], onCellClick: handleCellClick, columns: columnsForDataGrid, rowCount: totalLeads, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: "server", sortingMode: "server", sortModel: filters.sortModel, onSortModelChange: (model) => {
                            // Sort + page-reset in one update, so the grid issues a single
                            // request instead of one for the sort and one for the page.
                            setFilters((prev) => (Object.assign(Object.assign({}, prev), { sortModel: model, page: 0 })));
                        }, disableColumnMenu: true, disableColumnFilter: true, disableRowSelectionOnClick: true, localeText: { noRowsLabel: translate("Geen rijen"), noResultsOverlayLabel: translate("Geen resultaten") }, hideFooterPagination: !(leads === null || leads === void 0 ? void 0 : leads.length), rowHeight: 80, sx: {
                            border: "none",
                            fontSize: "14px",
                            height: {
                                xs: "400px",
                                md: "calc(100vh - 350px)",
                            },
                            // height:100,
                            "& .has-contract-row": {
                                backgroundColor: "#ffff",
                            },
                            "& .MuiDataGrid-columnHeaderTitle ": {
                                fontSize: "20px",
                                fontWeight: 400,
                            },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }, sortingOrder: ["asc", "desc"] }, "leads-grid") }) }), _jsx(AssignMultipleLeadsModal, { open: modalOpen, onClose: handleCloseModal, refresh: refreshModalData, setRefreshModalData: setRefreshModalData, contactOptions: contactOptions })] }));
};
